import 'reflect-metadata'; // only import this in main.ts
import Vue from 'vue';
import App from './app.vue';
import router from './router';
import store from './store';
import { containerBuilder } from './dependency-injection/app.container';
import AuthenticationModule, {
  AUTHENTICATION_MODULE_NAME
} from '@/store/modules/authentication.module';
import { getModule } from 'vuex-module-decorators';
import { routeName } from '@/constants/route.constants';
import { Role } from '@/constants/role.constants';
import moment from 'moment';

// Plugins
import './plugins/index';

// Filters
import './filters/index';

// Styles
import './app.scss';
import { AuthenticationGetters } from './constants/getters.constants';
import { AuthenticationActions } from './constants/actions.constants';

// TODO: Remove all console.log statements in production builds? https://stackoverflow.com/a/53326364

Vue.config.productionTip = false;

const authModule: AuthenticationModule = getModule(AuthenticationModule, store);

router.beforeEach((to, from, next) => {
  if (to.meta && to.meta.title) {
    document.title = `${to.meta.title} ${process.env.VUE_APP_TITLE}`;
  }

  const navRequiresAdmin = (roles: string[]) => {
    if (roles.includes(Role.Admin)) {
      next();
    } else {
      next({ name: routeName.login, replace: true });
    }
  };

  const navRequiresAuth = (roles: string[]) => {
    if (
      roles.includes(Role.Veterinarian) ||
      roles.includes(Role.VeterinarianUser)
    ) {
      next();
    } else {
      next({ name: routeName.login, replace: true });
    }
  };

  const navRequiresVeterinarian = (roles: string[]) => {
    if (roles.includes(Role.Veterinarian)) {
      next();
    } else {
      if (
        authModule.loginResponse?.token &&
        authModule.jwtExpires?.isAfter(moment())
      ) {
        next({ name: routeName.veterinarian.register, replace: true });
      } else {
        next({ name: routeName.login, replace: true });
      }
    }
  };

  if (to.meta && to.meta.requiresAdmin) {
    if (authModule.jwtRoles.length === 0) {
      const watcher = store.watch<string[]>(
        () => {
          const roles =
            store.getters[
              `${AUTHENTICATION_MODULE_NAME}/${AuthenticationGetters.jwtRoles}`
            ];

          if (roles.length === 0) {
            // don't await the watcher will catch it when it completes
            authModule[AuthenticationActions.refreshTokenAction]();
            return null;
          }

          return roles;
        },
        (jwtRoles) => {
          watcher(); // stop watching
          navRequiresAdmin(jwtRoles);
        }
      );
    } else {
      navRequiresAdmin(authModule.jwtRoles);
    }
  } else if (to.meta && to.meta.requiresAuth) {
    if (authModule.jwtRoles.length === 0) {
      const watcher = store.watch<string[]>(
        () => {
          const roles =
            store.getters[
              `${AUTHENTICATION_MODULE_NAME}/${AuthenticationGetters.jwtRoles}`
            ];

          if (roles.length === 0) {
            // don't await the watcher will catch it when it completes
            authModule[AuthenticationActions.refreshTokenAction]();
            return null;
          }

          return roles;
        },
        (jwtRoles) => {
          watcher(); // stop watching
          navRequiresAuth(jwtRoles);
        }
      );
    } else {
      navRequiresAuth(authModule.jwtRoles);
    }
  } else if (to.meta && to.meta.requiresVeterinarian) {
    if (authModule.jwtRoles.length === 0) {
      const watcher = store.watch<string[]>(
        () => {
          const roles =
            store.getters[
              `${AUTHENTICATION_MODULE_NAME}/${AuthenticationGetters.jwtRoles}`
            ];

          if (roles.length === 0) {
            // don't await the watcher will catch it when it completes
            authModule[AuthenticationActions.refreshTokenAction]();
            return null;
          }

          return roles;
        },
        (jwtRoles) => {
          watcher(); // stop watching
          navRequiresVeterinarian(jwtRoles);
        }
      );
    } else {
      navRequiresVeterinarian(authModule.jwtRoles);
    }
  } else if (to.meta && to.meta.requiresAcceptingTerms) {
    if (authModule.hasAcceptedTerms) {
      next();
    } else {
      next({ name: routeName.public.scan, replace: true });
    }
  } else if (to.meta && to.meta.requiresTagHasNoMatch) {
    if (!authModule.hasTagGottaMatch) {
      next();
    } else {
      next({ name: routeName.public.scan, replace: true });
    }
  } else if (to.meta && to.meta.requiresOwnerAuthentication) {
    if (authModule.hasOwnerAuthenticated) {
      next();
    } else {
      next({ name: routeName.public.scan, replace: true });
    }
  } else {
    next();
  }
});

export class AppModule {
  constructor() {
    containerBuilder();

    this.bootstrap();
  }

  private async bootstrap(): Promise<Vue> {
    return new Vue({
      router,
      store,
      render: (h) => h(App)
    }).$mount('#app');
  }
}

new AppModule();
